import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaPaperPlane, FaUsers, FaUserPlus, FaKey, FaCheck, FaTimes, FaSpinner, FaEye, FaArrowLeft, FaRocket, FaHistory, FaFilter } from 'react-icons/fa';

const AdminEmail = () => {
  const [activeTab, setActiveTab] = useState('custom');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // Custom Email State
  const [customEmail, setCustomEmail] = useState({
    recipients: '',
    subject: '',
    content: '',
    emailType: 'custom'
  });

  // Welcome Email State
  const [welcomeEmail, setWelcomeEmail] = useState({
    userIds: []
  });

  // Marketing Email State
  const [marketingEmail, setMarketingEmail] = useState({
    recipients: '',
    subject: '',
    campaignType: 'general',
    ctaText: 'Get Started',
    ctaUrl: 'https://appacts.com/register',
    customContent: ''
  });

  // Test Email State
  const [testEmail, setTestEmail] = useState({
    testEmail: ''
  });

  // Users for selection
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Email Logs State
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState({});
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFilters, setLogsFilters] = useState({
    emailType: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  const [logsPagination, setLogsPagination] = useState({
    current: 1,
    limit: 20,
    total: 0
  });

  useEffect(() => {
    fetchUsers();
    if (activeTab === 'logs') {
      fetchEmailLogs();
      fetchEmailStats();
    }
    // İlk yüklemede general marketing için varsayılan değerleri doldur
    const defaults = getCampaignDefaults('general');
    setMarketingEmail(prev => ({
      ...prev,
      subject: defaults.subject,
      customContent: defaults.content,
      ctaText: defaults.ctaText,
      ctaUrl: defaults.ctaUrl
    }));
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/dashboard/admin/users?limit=1000', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const showMessage = (msg, type = 'success') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  // Campaign type'a göre otomatik subject ve içerik oluştur
  const getCampaignDefaults = (campaignType) => {
    const defaults = {
      general: {
        subject: '🚀 Save 10+ Hours Weekly - Stop Managing 7+ Different Tools',
        content: 'Stop wasting time switching between multiple platforms. AppActs consolidates your entire app development workflow into one powerful solution, saving you 10+ hours every week while boosting productivity by 300%.',
        ctaText: 'Start Saving Time Now',
        ctaUrl: 'https://appacts.com'
      },
      'feature-launch': {
        subject: '✨ New Feature: [Feature Name] - Save 2+ Hours Per Week',
        content: 'We\'ve just launched a game-changing feature that will save you 2+ hours every week. This powerful addition automates what used to take hours of manual work.',
        ctaText: 'Try New Feature',
        ctaUrl: 'https://appacts.com'
      },
      'pricing-update': {
        subject: '💰 Limited Time: Get 3 Months Free - ROI Guaranteed',
        content: 'Get 3 months free when you upgrade today. Our users typically see 300% ROI within the first month by saving 10+ hours weekly.',
        ctaText: 'Claim Free Months',
        ctaUrl: 'https://appacts.com'
      },
      newsletter: {
        subject: '📰 Weekly Time-Saving Tips: How Developers Save 10+ Hours',
        content: 'This week\'s insights: How successful developers are saving 10+ hours weekly with AppActs. Plus exclusive tips you won\'t find anywhere else.',
        ctaText: 'Read Full Article',
        ctaUrl: 'https://appacts.com'
      },
      promotion: {
        subject: '🎉 Exclusive: 50% Off + 2 Months Free - Limited Time',
        content: 'For the next 48 hours only: Get 50% off your first year + 2 months free. That\'s 14 months for the price of 6. Save $500+ and 10+ hours weekly.',
        ctaText: 'Claim This Deal',
        ctaUrl: 'https://appacts.com'
      }
    };
    return defaults[campaignType] || defaults.general;
  };

  // Campaign type değiştiğinde otomatik doldur
  const handleCampaignTypeChange = (campaignType) => {
    const defaults = getCampaignDefaults(campaignType);
    setMarketingEmail(prev => ({
      ...prev,
      campaignType,
      // Campaign type değiştiğinde her zaman varsayılan değerleri kullan
      subject: defaults.subject,
      customContent: defaults.content,
      ctaText: defaults.ctaText,
      ctaUrl: defaults.ctaUrl
    }));
  };

  const generatePreview = (type, data) => {
    let preview = {
      subject: '',
      html: '',
      text: '',
      recipients: []
    };

    switch (type) {
      case 'custom':
        preview = {
          subject: data.subject,
          html: `
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="utf-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <title>${data.subject}</title>
              <style>
                body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
                .container { max-width: 600px; margin: 0 auto; padding: 20px; }
                .header { background: #4F46E5; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
                .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 8px 8px; }
                .footer { text-align: center; margin-top: 30px; color: #666; font-size: 14px; }
              </style>
            </head>
            <body>
              <div class="container">
                <div class="header">
                  <h1>AppActs</h1>
                </div>
                <div class="content">
                  <div>${data.content}</div>
                  <p>Best regards,<br>The AppActs Team</p>
                </div>
                <div class="footer">
                  <p>This email was sent from AppActs.</p>
                </div>
              </div>
            </body>
            </html>
          `,
          text: data.content.replace(/<[^>]*>/g, ''),
          recipients: data.recipients.split(',').map(email => email.trim()).filter(email => email)
        };
        break;
      
      case 'welcome':
        preview = {
          subject: 'Welcome to AppActs - The App Activities Platform for Vibe Coders',
          html: `
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="utf-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <title>Welcome to AppActs</title>
              <style>
                body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; }
                .container { max-width: 600px; margin: 0 auto; padding: 20px; }
                .header { background: linear-gradient(135deg, #4F46E5, #7C3AED); color: white; padding: 30px 20px; text-align: center; border-radius: 8px 8px 0 0; }
                .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 8px 8px; }
                .button { display: inline-block; background: #4F46E5; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 20px 0; font-weight: bold; }
                .footer { text-align: center; margin-top: 30px; color: #666; font-size: 14px; }
                .feature-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin: 20px 0; }
                .feature-item { background: white; padding: 15px; border-radius: 8px; border-left: 4px solid #4F46E5; }
                .feature-title { font-weight: bold; color: #4F46E5; margin-bottom: 5px; }
                .feature-desc { font-size: 14px; color: #666; }
                .hero-text { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
                .subtitle { font-size: 16px; opacity: 0.9; margin-bottom: 20px; }
                .pricing-highlight { background: linear-gradient(135deg, #10B981, #059669); color: white; padding: 20px; border-radius: 8px; margin: 20px 0; text-align: center; }
                .pricing-text { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
                .pricing-subtext { font-size: 14px; opacity: 0.9; }
              </style>
            </head>
            <body>
              <div class="container">
                <div class="header">
                  <div class="hero-text">Welcome to AppActs!</div>
                  <div class="subtitle">The App Activities Platform for Vibe Coders</div>
                </div>
                <div class="content">
                  <h2>Hello [User Name],</h2>
                  <p>Welcome to AppActs, your all-in-one platform for app development activities! We're excited to have you on board.</p>
                  
                  <p>AppActs is the comprehensive solution that brings together all the tools you need for your app development journey:</p>
                  
                  <div class="feature-grid">
                    <div class="feature-item">
                      <div class="feature-title">📄 Legal Documents</div>
                      <div class="feature-desc">Generate GDPR-compliant privacy policies, terms of service, and other legal documents in minutes.</div>
                    </div>
                    <div class="feature-item">
                      <div class="feature-title">🔗 Smart URL Shortener</div>
                      <div class="feature-desc">Create intelligent short links with platform-specific redirects and detailed analytics.</div>
                    </div>
                    <div class="feature-item">
                      <div class="feature-title">🎧 Customer Support</div>
                      <div class="feature-desc">Manage customer tickets, support modules, and provide professional customer service.</div>
                    </div>
                    <div class="feature-item">
                      <div class="feature-title">🚀 Internal Ads</div>
                      <div class="feature-desc">Create and manage internal advertisements to promote your products within your app.</div>
                    </div>
                  </div>
                  
                  <div class="pricing-highlight">
                    <div class="pricing-text">All-in-One Platform</div>
                    <div class="pricing-subtext">Instead of managing 7 different platforms, use just one!</div>
                  </div>
                  
                  <p>Ready to get started? Click the button below to access your dashboard:</p>
                  <a href="https://appacts.com/login" class="button">Access Dashboard</a>
                  
                  <p>If you have any questions or need assistance, don't hesitate to contact our support team.</p>
                  
                  <p>Best regards,<br>The AppActs Team</p>
                </div>
                <div class="footer">
                  <p>This email was sent from <strong>appacts.com</strong>. If you didn't create an account, please ignore this email.</p>
                </div>
              </div>
            </body>
            </html>
          `,
          text: `Welcome to AppActs - The App Activities Platform for Vibe Coders!\n\nHello [User Name],\n\nWelcome to AppActs, your all-in-one platform for app development activities! We're excited to have you on board.\n\nAppActs is the comprehensive solution that brings together all the tools you need for your app development journey:\n\n📄 Legal Documents\nGenerate GDPR-compliant privacy policies, terms of service, and other legal documents in minutes.\n\n🔗 Smart URL Shortener\nCreate intelligent short links with platform-specific redirects and detailed analytics.\n\n🎧 Customer Support\nManage customer tickets, support modules, and provide professional customer service.\n\n🚀 Internal Ads\nCreate and manage internal advertisements to promote your products within your app.\n\nAll-in-One Platform\nInstead of managing 7 different platforms, use just one!\n\nReady to get started? Visit: https://appacts.com/login\n\nIf you have any questions or need assistance, don't hesitate to contact our support team.\n\nBest regards,\nThe AppActs Team\n\nThis email was sent from appacts.com. If you didn't create an account, please ignore this email.`,
          recipients: selectedUsers.map(user => user.email)
        };
        break;
      
      case 'marketing':
        const campaignDefaults = getCampaignDefaults(marketingEmail.campaignType);
        const emailContent = marketingEmail.customContent || campaignDefaults.content;
        preview = {
          subject: marketingEmail.subject || campaignDefaults.subject,
          html: `
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="utf-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <title>${marketingEmail.subject || campaignDefaults.subject}</title>
              <style>
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
                
                * { box-sizing: border-box; }
                body { 
                  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; 
                  line-height: 1.6; 
                  color: #ffffff; 
                  margin: 0; 
                  padding: 0; 
                  background: linear-gradient(135deg, #000000 0%, #1a0b2e 25%, #16213e 50%, #0f3460 75%, #000000 100%);
                  min-height: 100vh;
                }
                
                .email-container { 
                  max-width: 650px; 
                  margin: 0 auto; 
                  background: rgba(0, 0, 0, 0.8);
                  backdrop-filter: blur(20px);
                  border-radius: 24px;
                  overflow: hidden;
                  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
                  border: 1px solid rgba(255, 255, 255, 0.1);
                }
                
                .header { 
                  background: linear-gradient(135deg, #000000 0%, #1a0b2e 50%, #16213e 100%);
                  color: white; 
                  padding: 60px 40px; 
                  text-align: center; 
                  position: relative;
                  overflow: hidden;
                }
                
                .header::before {
                  content: '';
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background: radial-gradient(circle at 30% 20%, rgba(139, 69, 255, 0.3) 0%, transparent 50%),
                              radial-gradient(circle at 70% 80%, rgba(59, 130, 246, 0.3) 0%, transparent 50%);
                  pointer-events: none;
                }
                
                .header-content {
                  position: relative;
                  z-index: 1;
                }
                
                .hero-text { 
                  font-size: 42px; 
                  font-weight: 800; 
                  margin-bottom: 16px; 
                  letter-spacing: -0.02em;
                  background: linear-gradient(135deg, #ffffff 0%, #e0e7ff 50%, #c7d2fe 100%);
                  -webkit-background-clip: text;
                  -webkit-text-fill-color: transparent;
                  background-clip: text;
                }
                
                .subtitle { 
                  font-size: 18px; 
                  opacity: 0.9; 
                  margin-bottom: 32px; 
                  font-weight: 400;
                  color: rgba(255, 255, 255, 0.8);
                }
                
                .badge {
                  display: inline-flex;
                  align-items: center;
                  gap: 8px;
                  padding: 12px 24px;
                  background: rgba(16, 185, 129, 0.1);
                  border: 1px solid rgba(16, 185, 129, 0.3);
                  border-radius: 50px;
                  font-size: 14px;
                  font-weight: 600;
                  color: #10b981;
                  backdrop-filter: blur(10px);
                }
                
                .badge-dot {
                  width: 8px;
                  height: 8px;
                  background: #10b981;
                  border-radius: 50%;
                  animation: pulse 2s infinite;
                }
                
                @keyframes pulse {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.5; }
                }
                
                .content { 
                  background: rgba(0, 0, 0, 0.6);
                  padding: 50px 40px 0 40px; 
                  backdrop-filter: blur(20px);
                }
                
                .custom-content { 
                  background: rgba(255, 255, 255, 0.05); 
                  padding: 32px; 
                  border-radius: 16px; 
                  margin: 32px 0; 
                  border: 1px solid rgba(139, 92, 246, 0.3);
                  backdrop-filter: blur(10px);
                  box-shadow: 0 8px 32px rgba(139, 92, 246, 0.1);
                }
                
                .custom-content h2 { 
                  color: #ffffff; 
                  font-size: 24px; 
                  font-weight: 700; 
                  margin-bottom: 20px; 
                  margin-top: 0;
                }
                
                .custom-content p {
                  color: rgba(255, 255, 255, 0.9);
                  font-size: 16px;
                  line-height: 1.7;
                  margin: 0;
                }
                
                .feature-grid { 
                  display: flex; 
                  flex-direction: column; 
                  gap: 24px; 
                  margin: 40px 0; 
                }
                
                .feature-item { 
                  background: rgba(255, 255, 255, 0.05); 
                  padding: 28px; 
                  border-radius: 16px; 
                  border: 1px solid rgba(59, 130, 246, 0.3);
                  backdrop-filter: blur(10px);
                  transition: all 0.3s ease;
                  box-shadow: 0 8px 32px rgba(59, 130, 246, 0.1);
                  display: flex;
                  align-items: flex-start;
                  gap: 20px;
                }
                
                .feature-item:nth-child(odd) {
                  flex-direction: row;
                  text-align: left;
                }
                
                .feature-item:nth-child(even) {
                  flex-direction: row-reverse;
                  text-align: right;
                }
                
                .feature-content {
                  flex: 1;
                  display: flex;
                  flex-direction: column;
                  gap: 8px;
                }
                
                .feature-icon {
                  width: 60px;
                  height: 60px;
                  background: rgba(255, 255, 255, 0.1);
                  border-radius: 12px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 24px;
                  flex-shrink: 0;
                }
                
                .feature-item:hover {
                  transform: translateY(-4px);
                  background: rgba(255, 255, 255, 0.08);
                }
                
                .feature-title { 
                  font-weight: 600; 
                  color: #ffffff; 
                  margin-bottom: 12px; 
                  font-size: 18px;
                }
                
                .feature-desc { 
                  font-size: 15px; 
                  color: rgba(255, 255, 255, 0.7); 
                  line-height: 1.6; 
                }
                
                .pricing-highlight { 
                  background: linear-gradient(135deg, #10b981 0%, #059669 100%); 
                  color: white; 
                  padding: 32px; 
                  border-radius: 16px; 
                  margin: 40px 0; 
                  text-align: center;
                  box-shadow: 0 10px 25px rgba(16, 185, 129, 0.3);
                }
                
                .pricing-text { 
                  font-size: 24px; 
                  font-weight: 700; 
                  margin-bottom: 8px; 
                }
                
                .pricing-subtext { 
                  font-size: 16px; 
                  opacity: 0.9; 
                  font-weight: 400; 
                }
                
                .cta-section { 
                  text-align: center; 
                  margin: 50px 0; 
                }
                
                .cta-button { 
                  display: inline-block; 
                  background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%); 
                  color: #000000; 
                  padding: 18px 36px; 
                  text-decoration: none; 
                  border-radius: 12px; 
                  font-weight: 600; 
                  font-size: 16px;
                  box-shadow: 0 10px 25px rgba(255, 255, 255, 0.2);
                  transition: all 0.3s ease;
                  border: 1px solid rgba(255, 255, 255, 0.2);
                }
                
                .cta-button:hover {
                  transform: translateY(-2px);
                  box-shadow: 0 15px 35px rgba(255, 255, 255, 0.3);
                }
                
                .video-section {
                  margin: 40px 0;
                  text-align: center;
                }
                
                .video-container {
                  position: relative;
                  width: 100%;
                  max-width: 500px;
                  margin: 0 auto;
                  border-radius: 16px;
                  overflow: hidden;
                  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
                }
                
                .video-thumbnail {
                  width: 100%;
                  height: 280px;
                  background: linear-gradient(135deg, #1a0b2e 0%, #16213e 100%);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  position: relative;
                  cursor: pointer;
                }
                
                .play-button {
                  width: 80px;
                  height: 80px;
                  background: rgba(255, 255, 255, 0.9);
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  transition: all 0.3s ease;
                }
                
                .play-button:hover {
                  transform: scale(1.1);
                  background: rgba(255, 255, 255, 1);
                }
                
                .play-icon {
                  width: 0;
                  height: 0;
                  border-left: 24px solid #000000;
                  border-top: 16px solid transparent;
                  border-bottom: 16px solid transparent;
                  margin-left: 6px;
                }
                
                .video-title {
                  margin-top: 20px;
                  font-size: 18px;
                  font-weight: 600;
                  color: #ffffff;
                }
                
                .company-info { 
                  background: rgba(255, 255, 255, 0.05); 
                  padding: 32px; 
                  border-radius: 16px; 
                  margin: 40px 0; 
                  text-align: center;
                  backdrop-filter: blur(10px);
                }
                
                .company-name { 
                  font-size: 24px; 
                  font-weight: 700; 
                  color: #ffffff; 
                  margin-bottom: 8px;
                  background: linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%);
                  -webkit-background-clip: text;
                  -webkit-text-fill-color: transparent;
                  background-clip: text;
                }
                
                .company-tagline { 
                  font-size: 16px; 
                  color: rgba(255, 255, 255, 0.7);
                  font-weight: 400;
                }
                
                .footer { 
                  background: linear-gradient(135deg, #000000 0%, #1a0b2e 50%, #16213e 100%);
                  color: rgba(255, 255, 255, 0.8); 
                  padding: 40px 40px 50px 40px; 
                  text-align: center; 
                  position: relative;
                  overflow: hidden;
                }
                
                .footer::before {
                  content: '';
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background: radial-gradient(circle at 30% 20%, rgba(139, 69, 255, 0.2) 0%, transparent 50%),
                              radial-gradient(circle at 70% 80%, rgba(59, 130, 246, 0.2) 0%, transparent 50%);
                  pointer-events: none;
                }
                
                .footer-content {
                  position: relative;
                  z-index: 1;
                }
                
                .footer p {
                  margin: 8px 0;
                  font-size: 14px;
                  color: rgba(255, 255, 255, 0.8);
                }
                
                .footer a {
                  color: #3b82f6;
                  text-decoration: none;
                }
                
                .footer a:hover {
                  text-decoration: underline;
                }
                
                @media (max-width: 600px) {
                  .email-container {
                    margin: 10px;
                    border-radius: 16px;
                  }
                  
                  .header {
                    padding: 40px 20px;
                  }
                  
                  .hero-text {
                    font-size: 32px;
                  }
                  
                  .content {
                    padding: 30px 20px;
                  }
                  
                  .feature-grid {
                    grid-template-columns: 1fr;
                    gap: 16px;
                  }
                  
                  .custom-content {
                    padding: 24px;
                  }
                  
                  .cta-button {
                    padding: 16px 32px;
                    font-size: 15px;
                  }
                }
              </style>
            </head>
            <body>
              <div class="email-container">
                <div class="header">
                  <div class="header-content">
                    <div class="badge">
                      <div class="badge-dot"></div>
                      <span>All in One Platform</span>
                    </div>
                  <div class="hero-text">AppActs</div>
                    <div class="subtitle">The App Activities Platform for Vibe Coders</div>
                </div>
                </div>
                
                <div class="content">
                  ${emailContent ? `
                  <div class="custom-content">
                    <h2>
                      ${marketingEmail.campaignType === 'general' ? '🚀 Platform Overview' : 
                        marketingEmail.campaignType === 'feature-launch' ? '✨ New Feature Release' :
                        marketingEmail.campaignType === 'pricing-update' ? '💰 Special Offer' :
                        marketingEmail.campaignType === 'newsletter' ? '📰 Industry Insights' :
                        marketingEmail.campaignType === 'promotion' ? '⚡ Limited Time Offer' : '📢 Latest Updates'}
                    </h2>
                    <p>${emailContent}</p>
                  </div>
                  ` : ''}
                  
                  ${marketingEmail.campaignType === 'general' ? `
                  <p style="font-size: 18px; font-weight: 500; color: rgba(255, 255, 255, 0.9); margin-bottom: 32px;">
                    <strong>Streamline your development workflow</strong> with our comprehensive platform that integrates essential tools into one professional solution:
                  </p>
                  
                  <div class="feature-grid">
                    <div class="feature-item">
                      <div class="feature-icon">📄</div>
                      <div class="feature-content">
                      <div class="feature-title">Legal Document Generator</div>
                      <div class="feature-desc">Generate GDPR-compliant privacy policies, terms of service, and other legal documents in minutes.</div>
                      </div>
                    </div>
                    <div class="feature-item">
                      <div class="feature-icon">🔗</div>
                      <div class="feature-content">
                      <div class="feature-title">Smart URL Shortener</div>
                      <div class="feature-desc">Create intelligent short links with platform-specific redirects and detailed click analytics.</div>
                      </div>
                    </div>
                    <div class="feature-item">
                      <div class="feature-icon">🎧</div>
                      <div class="feature-content">
                      <div class="feature-title">Customer Support System</div>
                      <div class="feature-desc">Manage customer tickets, support modules, and provide professional customer service.</div>
                      </div>
                    </div>
                    <div class="feature-item">
                      <div class="feature-icon">🚀</div>
                      <div class="feature-content">
                      <div class="feature-title">Internal Ads Management</div>
                      <div class="feature-desc">Create and manage internal advertisements to promote your products within your app.</div>
                      </div>
                    </div>
                    <div class="feature-item">
                      <div class="feature-icon">🌐</div>
                      <div class="feature-content">
                        <div class="feature-title">Landing Pages</div>
                        <div class="feature-desc">Create beautiful, responsive landing pages to showcase your products and convert visitors into customers.</div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="video-section">
                    <div class="video-container">
                      <div class="video-thumbnail" onclick="window.open('https://www.youtube.com/watch?v=WSvJyzmPy80&list=PLD2iaot8uUmED6GR1h8SFaUW1zwO-CqdB', '_blank')">
                        <div class="play-button">
                          <div class="play-icon"></div>
                  </div>
                      </div>
                    </div>
                    <div class="video-title">See AppActs in Action</div>
                  </div>
                  
                  ` : ''}
                  
                  <div class="cta-section">
                    <p style="font-size: 20px; font-weight: 600; color: rgba(255, 255, 255, 0.9); margin-bottom: 24px;">
                      <strong>Ready to enhance your development process?</strong>
                    </p>
                    <a href="${marketingEmail.ctaUrl || campaignDefaults.ctaUrl}" class="cta-button">${marketingEmail.ctaText || campaignDefaults.ctaText}</a>
                  </div>
                  
                  <p style="color: rgba(255, 255, 255, 0.8); font-size: 16px; margin-top: 32px;">Best regards,<br>The AppActs Team</p>
                  </div>
                  
                <div class="footer">
                  <div class="footer-content">
                    <p>This email was sent from <strong>appacts.com</strong></p>
                    <p>If you no longer wish to receive these emails, you can <a href="https://appacts.com/unsubscribe?email=example@email.com">unsubscribe here</a></p>
                  </div>
                </div>
              </div>
            </body>
            </html>
          `,
          text: `${marketingEmail.subject || campaignDefaults.subject}\n\n${emailContent}\n\n${marketingEmail.campaignType === 'general' ? `Streamline your development workflow with our comprehensive platform:\n\n📄 Legal Document Generator - Generate GDPR-compliant privacy policies, terms of service, and other legal documents in minutes.\n🔗 Smart URL Shortener - Create intelligent short links with platform-specific redirects and detailed click analytics.\n🎧 Customer Support System - Manage customer tickets, support modules, and provide professional customer service.\n🚀 Internal Ads Management - Create and manage internal advertisements to promote your products within your app.\n🌐 Landing Pages - Create beautiful, responsive landing pages to showcase your products and convert visitors into customers.\n\nWatch our demo video: https://www.youtube.com/watch?v=WSvJyzmPy80&list=PLD2iaot8uUmED6GR1h8SFaUW1zwO-CqdB\n\n` : ''}Ready to enhance your development process?\n${marketingEmail.ctaUrl || campaignDefaults.ctaUrl}\n\nBest regards,\nThe AppActs Team\n\nThis email was sent from appacts.com.\nIf you no longer wish to receive these emails, you can unsubscribe at https://appacts.com/unsubscribe?email=example@email.com.`,
          recipients: marketingEmail.recipients.split(',').map(email => email.trim()).filter(email => email)
        };
        break;
    }

    return preview;
  };

  const handlePreview = (type) => {
    let data = {};
    
    switch (type) {
      case 'custom':
        data = customEmail;
        break;
      case 'welcome':
        data = { selectedUsers };
        break;
      case 'password-reset':
        data = { selectedUsers };
        break;
    }

    const preview = generatePreview(type, data);
    setPreviewData(preview);
    setShowPreview(true);
  };

  const handleCustomEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const recipients = customEmail.recipients.split(',').map(email => email.trim()).filter(email => email);
      
      const response = await fetch('/api/dashboard/admin/send-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipients,
          subject: customEmail.subject,
          content: customEmail.content,
          emailType: customEmail.emailType
        })
      });

      const data = await response.json();

      if (response.ok) {
        showMessage(`Email sent successfully! ${data.summary.successful} successful, ${data.summary.failed} failed.`, 'success');
        setCustomEmail({ recipients: '', subject: '', content: '', emailType: 'custom' });
      } else {
        showMessage(data.message || 'Failed to send email', 'error');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      showMessage('Error sending email', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleWelcomeEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const userIds = selectedUsers.map(user => user._id);
      
      const response = await fetch('/api/dashboard/admin/send-welcome-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userIds })
      });

      const data = await response.json();

      if (response.ok) {
        showMessage(`Welcome emails sent! ${data.summary.successful} successful, ${data.summary.failed} failed.`, 'success');
        setSelectedUsers([]);
      } else {
        showMessage(data.message || 'Failed to send welcome emails', 'error');
      }
    } catch (error) {
      console.error('Error sending welcome emails:', error);
      showMessage('Error sending welcome emails', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMarketingEmailSubmit = async (e) => {
    e.preventDefault();
    if (!marketingEmail.recipients || !marketingEmail.subject) {
      showMessage('Please fill in all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
             const response = await fetch('/api/dashboard/admin/send-marketing-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          recipients: marketingEmail.recipients,
          subject: marketingEmail.subject,
          campaignType: marketingEmail.campaignType,
          customContent: marketingEmail.customContent,
          ctaText: marketingEmail.ctaText,
          ctaUrl: marketingEmail.ctaUrl
        })
      });

      const data = await response.json();
      if (response.ok) {
        showMessage(`Marketing email sent to ${marketingEmail.recipients.split(',').length} recipients`, 'success');
        setMarketingEmail({
          recipients: '',
          subject: '',
          campaignType: 'general',
          customContent: '',
          ctaText: 'Get Started',
          ctaUrl: 'https://appacts.com'
        });
      } else {
        showMessage(data.message || 'Failed to send marketing email', 'error');
      }
    } catch (error) {
      showMessage('Error sending marketing email', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const userIds = selectedUsers.map(user => user._id);
      
      const response = await fetch('/api/dashboard/admin/send-password-reset', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userIds })
      });

      const data = await response.json();

      if (response.ok) {
        showMessage(`Password reset emails sent! ${data.summary.successful} successful, ${data.summary.failed} failed.`, 'success');
        setSelectedUsers([]);
      } else {
        showMessage(data.message || 'Failed to send password reset emails', 'error');
      }
    } catch (error) {
      console.error('Error sending password reset emails:', error);
      showMessage('Error sending password reset emails', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/dashboard/admin/test-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ testEmail: testEmail.testEmail })
      });

      const data = await response.json();

      if (response.ok) {
        showMessage('Test email sent successfully!', 'success');
        setTestEmail({ testEmail: '' });
      } else {
        showMessage(data.message || 'Failed to send test email', 'error');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      showMessage('Error sending test email', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u._id === user._id);
      if (isSelected) {
        return prev.filter(u => u._id !== user._id);
      } else {
        return [...prev, user];
      }
    });
  };

  const selectAllUsers = () => {
    setSelectedUsers(users);
  };

  const clearSelection = () => {
    setSelectedUsers([]);
  };

  const fetchEmailLogs = async () => {
    setLogsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams({
        page: logsPagination.current,
        limit: logsPagination.limit,
        ...logsFilters
      });

      const response = await fetch(`/api/admin/email-logs?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        setEmailLogs(data.emails);
        setLogsPagination(prev => ({
          ...prev,
          total: data.total
        }));
      } else {
        showMessage(data.message || 'Failed to fetch email logs', 'error');
      }
    } catch (error) {
      console.error('Error fetching email logs:', error);
      showMessage('Error fetching email logs', 'error');
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchEmailStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams({
        ...logsFilters
      });

      const response = await fetch(`/api/admin/email-stats?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        setEmailStats(data);
      }
    } catch (error) {
      console.error('Error fetching email stats:', error);
    }
  };

  const handleLogsFilterChange = (filter, value) => {
    setLogsFilters(prev => ({
      ...prev,
      [filter]: value
    }));
    setLogsPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const applyLogsFilters = () => {
    fetchEmailLogs();
    fetchEmailStats();
  };

  const clearLogsFilters = () => {
    setLogsFilters({
      emailType: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
    setLogsPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'text-green-400';
      case 'delivered': return 'text-blue-400';
      case 'failed': return 'text-red-400';
      case 'bounced': return 'text-yellow-400';
      case 'pending': return 'text-gray-400';
      default: return 'text-gray-400';
    }
  };

  const getEmailTypeLabel = (type) => {
    switch (type) {
      case 'custom': return 'Custom';
      case 'welcome': return 'Welcome';
      case 'marketing': return 'Marketing';
      case 'support-summary': return 'Support Summary';
      case 'verification': return 'Verification';
      default: return 'Other';
    }
  };

  const tabs = [
    { id: 'custom', label: 'Custom Email', icon: FaEnvelope },
    { id: 'welcome', label: 'Welcome Email', icon: FaUserPlus },
    { id: 'marketing', label: 'Marketing Campaign', icon: FaRocket },
    { id: 'logs', label: 'Email Logs', icon: FaHistory }
  ];

  return (
    <div className="space-y-6">

      {/* Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">Email Preview</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-white"
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Subject:</label>
                  <div className="bg-gray-700 p-3 rounded-lg text-white">{previewData.subject}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Recipients:</label>
                  <div className="bg-gray-700 p-3 rounded-lg text-white">
                    {previewData.recipients.join(', ')}
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email Content:</label>
                <div className="bg-white rounded-lg p-4 border">
                  <div dangerouslySetInnerHTML={{ __html: previewData.html }} />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-700">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center space-x-2 ${
          messageType === 'success' ? 'bg-green-900/20 text-green-400' : 'bg-red-900/20 text-red-400'
        }`}>
          {messageType === 'success' ? <FaCheck /> : <FaTimes />}
          <span>{message}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-gray-800 rounded-lg">
        <div className="border-b border-gray-700">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Custom Email Tab */}
          {activeTab === 'custom' && (
            <form onSubmit={handleCustomEmailSubmit} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 text-left">
                    Recipients (comma-separated email addresses)
                  </label>
                  <textarea
                    value={customEmail.recipients}
                    onChange={(e) => setCustomEmail({ ...customEmail, recipients: e.target.value })}
                    placeholder="user1@example.com, user2@example.com, user3@example.com"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 text-left">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={customEmail.subject}
                    onChange={(e) => setCustomEmail({ ...customEmail, subject: e.target.value })}
                    placeholder="Email subject"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 text-left">
                  Content (HTML supported)
                </label>
                <textarea
                  value={customEmail.content}
                  onChange={(e) => setCustomEmail({ ...customEmail, content: e.target.value })}
                  placeholder="Enter your email content here. HTML tags are supported."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="8"
                  required
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => handlePreview('custom')}
                  disabled={!customEmail.subject || !customEmail.content || !customEmail.recipients}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <FaEye />
                  <span>Preview</span>
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  {loading ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
                  <span>{loading ? 'Sending...' : 'Send Email'}</span>
                </button>
              </div>
            </form>
          )}

          {/* Marketing Campaign Tab */}
          {activeTab === 'marketing' && (
            <div className="space-y-6">
              <div className="bg-gray-800 rounded-lg p-6">

                <form onSubmit={handleMarketingEmailSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2 text-left">
                        Recipients (Email addresses, comma-separated)
                      </label>
                      <textarea
                        value={marketingEmail.recipients}
                        onChange={(e) => setMarketingEmail({...marketingEmail, recipients: e.target.value})}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="user1@example.com, user2@example.com, user3@example.com"
                        rows="3"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2 text-left">
                          Email Subject
                        </label>
                        <input
                          type="text"
                          value={marketingEmail.subject}
                          onChange={(e) => setMarketingEmail({...marketingEmail, subject: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Discover AppActs - The All-in-One Platform for App Developers"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2 text-left">
                          Campaign Type
                        </label>
                        <select
                          value={marketingEmail.campaignType}
                          onChange={(e) => handleCampaignTypeChange(e.target.value)}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="general">General Marketing</option>
                          <option value="feature-launch">Feature Launch</option>
                          <option value="pricing-update">Pricing Update</option>
                          <option value="newsletter">Newsletter</option>
                          <option value="promotion">Special Promotion</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email Content
                      </label>
                      <textarea
                        value={marketingEmail.customContent}
                        onChange={(e) => setMarketingEmail({...marketingEmail, customContent: e.target.value})}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter your custom email content here..."
                        rows="6"
                        required
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        This content will be displayed prominently in the email. You can customize it for your specific campaign.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          CTA Button Text
                        </label>
                        <input
                          type="text"
                          value={marketingEmail.ctaText}
                          onChange={(e) => setMarketingEmail({...marketingEmail, ctaText: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Get Started"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          CTA Button URL
                        </label>
                        <input
                          type="url"
                          value={marketingEmail.ctaUrl}
                          onChange={(e) => setMarketingEmail({...marketingEmail, ctaUrl: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://appacts.com/register"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={() => handlePreview('marketing')}
                      disabled={!marketingEmail.subject || !marketingEmail.recipients}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                    >
                      <FaEye />
                      <span>Preview</span>
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                    >
                      {loading ? <FaSpinner className="animate-spin" /> : <FaRocket />}
                      <span>{loading ? 'Sending...' : 'Send Marketing Email'}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Welcome Email Tab */}
          {activeTab === 'welcome' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-white">Select Users to Send Welcome Email</h3>
                <div className="space-x-2">
                  <button
                    onClick={selectAllUsers}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded"
                  >
                    Select All
                  </button>
                  <button
                    onClick={clearSelection}
                    className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded"
                  >
                    Clear Selection
                  </button>
                </div>
              </div>

              <div className="max-h-64 overflow-y-auto border border-gray-600 rounded-lg">
                {users.map((user) => {
                  const isSelected = selectedUsers.some(u => u._id === user._id);
                  return (
                    <div
                      key={user._id}
                      onClick={() => toggleUserSelection(user)}
                      className={`p-3 border-b border-gray-600 last:border-b-0 cursor-pointer hover:bg-gray-700 ${
                        isSelected ? 'bg-blue-900/20' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-white font-medium">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-gray-400 text-sm">{user.email}</div>
                        </div>
                        {isSelected && <FaCheck className="text-blue-400" />}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => handlePreview('welcome')}
                  disabled={selectedUsers.length === 0}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <FaEye />
                  <span>Preview</span>
                </button>
                <button
                  type="button"
                  onClick={handleWelcomeEmailSubmit}
                  disabled={loading || selectedUsers.length === 0}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  {loading ? <FaSpinner className="animate-spin" /> : <FaUserPlus />}
                  <span>
                    {loading ? 'Sending...' : `Send Welcome Email (${selectedUsers.length} users)`}
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Email Logs Tab */}
          {activeTab === 'logs' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Emails</p>
                      <p className="text-2xl font-bold text-white">{emailStats.total || 0}</p>
                    </div>
                    <FaEnvelope className="h-8 w-8 text-blue-500" />
                  </div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Sent</p>
                      <p className="text-2xl font-bold text-green-400">{emailStats.sent || 0}</p>
                    </div>
                    <FaCheck className="h-8 w-8 text-green-500" />
                  </div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Failed</p>
                      <p className="text-2xl font-bold text-red-400">{emailStats.failed || 0}</p>
                    </div>
                    <FaTimes className="h-8 w-8 text-red-500" />
                  </div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Pending</p>
                      <p className="text-2xl font-bold text-yellow-400">{emailStats.pending || 0}</p>
                    </div>
                    <FaSpinner className="h-8 w-8 text-yellow-500" />
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="bg-gray-800 rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <FaFilter className="h-5 w-5 text-gray-400" />
                  <h3 className="text-lg font-medium text-white">Filters</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email Type</label>
                    <select
                      value={logsFilters.emailType}
                      onChange={(e) => handleLogsFilterChange('emailType', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Types</option>
                      <option value="custom">Custom</option>
                      <option value="welcome">Welcome</option>
                      <option value="marketing">Marketing</option>
                      <option value="password-reset">Password Reset</option>
                      <option value="test">Test</option>
                      <option value="support-summary">Support Summary</option>
                      <option value="verification">Verification</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                    <select
                      value={logsFilters.status}
                      onChange={(e) => handleLogsFilterChange('status', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Status</option>
                      <option value="sent">Sent</option>
                      <option value="delivered">Delivered</option>
                      <option value="failed">Failed</option>
                      <option value="bounced">Bounced</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">From Date</label>
                    <input
                      type="date"
                      value={logsFilters.dateFrom}
                      onChange={(e) => handleLogsFilterChange('dateFrom', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">To Date</label>
                    <input
                      type="date"
                      value={logsFilters.dateTo}
                      onChange={(e) => handleLogsFilterChange('dateTo', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex space-x-3 mt-4">
                  <button
                    type="button"
                    onClick={applyLogsFilters}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <FaFilter />
                    <span>Apply Filters</span>
                  </button>
                  <button
                    type="button"
                    onClick={clearLogsFilters}
                    className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>

              {/* Email Logs Table */}
              <div className="bg-gray-800 rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-700">
                  <h3 className="text-lg font-medium text-white">Email Logs</h3>
                </div>
                {logsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <FaSpinner className="animate-spin h-8 w-8 text-blue-500" />
                    <span className="ml-2 text-gray-400">Loading email logs...</span>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-700">
                      <thead className="bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Recipient</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Subject</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Sent By</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Sent At</th>
                        </tr>
                      </thead>
                      <tbody className="bg-gray-800 divide-y divide-gray-700">
                        {emailLogs.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                              No email logs found
                            </td>
                          </tr>
                        ) : (
                          emailLogs.map((log) => (
                            <tr key={log._id} className="hover:bg-gray-700">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-white">{log.to}</div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-white max-w-xs truncate" title={log.subject}>
                                  {log.subject}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-600 text-gray-300">
                                  {getEmailTypeLabel(log.emailType)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`text-sm font-medium ${getStatusColor(log.status)}`}>
                                  {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-300">
                                  {log.sentBy?.firstName} {log.sentBy?.lastName}
                                </div>
                                <div className="text-xs text-gray-400">{log.sentByEmail}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                {formatDate(log.sentAt)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Pagination */}
                {emailLogs.length > 0 && (
                  <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
                    <div className="text-sm text-gray-400">
                      Showing {((logsPagination.current - 1) * logsPagination.limit) + 1} to {Math.min(logsPagination.current * logsPagination.limit, logsPagination.total)} of {logsPagination.total} results
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setLogsPagination(prev => ({ ...prev, current: prev.current - 1 }));
                          fetchEmailLogs();
                        }}
                        disabled={logsPagination.current === 1}
                        className="px-3 py-1 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:text-gray-500 text-white rounded transition-colors"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => {
                          setLogsPagination(prev => ({ ...prev, current: prev.current + 1 }));
                          fetchEmailLogs();
                        }}
                        disabled={logsPagination.current * logsPagination.limit >= logsPagination.total}
                        className="px-3 py-1 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:text-gray-500 text-white rounded transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEmail;
